import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCatalog } from "../content/Catalog";
import Discussion from "./Discussion";
import "./ComicReader.css";

export default function ComicReader() {
  const { id } = useParams();
  const { items, loading } = useCatalog();
  const comic = items.find((item) => item.type === 'comics' && item.id === id);
  if (!comic && loading) return <main className="reader"><p role="status">Loading comic…</p></main>;
  if (!comic) return <main className="reader"><h1>Comic not found</h1><Link to="/comics">All comics</Link></main>;
  return <Reader key={id} comic={comic} />;
}

function ComicImage({ src, number, eager = false, compact = false }) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return <figure className="reader-sheet">{failed ? <div className="reader-error"><p>Page {number} could not load.</p><button onClick={() => { setFailed(false); setAttempt(attempt + 1); }}>Try again</button></div> : <img key={attempt} src={src} alt={`Comic page ${number}`} loading={eager ? "eager" : "lazy"} decoding="async" onError={() => setFailed(true)} />}<figcaption>{compact ? number : `Page ${number}`}</figcaption></figure>;
}

function Reader({ comic }) {
  const [params, setParams] = useSearchParams();
  const total = comic.pages.length;
  const page = Math.max(1, Math.min(total, Number.parseInt(params.get("page"), 10) || 1));
  const mode = ["page", "spread", "scroll"].includes(params.get("view")) ? params.get("view") : "page";
  const [jump, setJump] = useState(String(page));
  const [shareStatus, setShareStatus] = useState("");
  const [visiblePage, setVisiblePage] = useState(page);
  const url = (number) => comic.pages[number - 1];
  const navigate = (number, view = mode) => setParams({ page: String(Math.max(1, Math.min(total, number))), view }, { replace: true });
  useEffect(() => { setJump(String(page)); }, [page]);
  useEffect(() => {
    // Keep the next three pages and the previous page warm without fetching the whole book.
    const images = [];
    const center = mode === 'scroll' ? visiblePage : page;
    for (let number = Math.max(1, center - 1); number <= Math.min(total, center + 3); number++) {
      const image = new Image();
      image.src = comic.pages[number - 1];
      images.push(image);
    }
    return () => { images.forEach((image) => { image.onload = null; }); };
  }, [page, total, comic.pages, mode, visiblePage]);
  useEffect(() => {
    if (mode !== 'scroll' || !window.IntersectionObserver) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) setVisiblePage(Number(visible[0].target.id.replace('comic-page-', '')));
    }, { rootMargin: '0px 0px -30% 0px' });
    document.querySelectorAll('.reader-pages > div').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [mode]);
  useEffect(() => {
    if (mode === "scroll") document.getElementById(`comic-page-${page}`)?.scrollIntoView({ block: "start" });
  }, [page, mode]);
  const current = mode === "scroll" ? visiblePage : page;
  const step = mode === "spread" ? 2 : 1;
  async function share() {
    try {
      const address = new URL(window.location.href);
      address.hash = `/comics/${comic.id}?page=${current}&view=${mode}`;
      if (navigator.share) await navigator.share({ title: comic.title, url: address.href });
      else { await navigator.clipboard.writeText(address.href); setShareStatus("Link copied."); }
    } catch (error) { if (error.name !== "AbortError") setShareStatus("Copy the page address from your browser to share it."); }
  }
  const controls = <div className="reader-controls"><button onClick={() => navigate(1)} disabled={current === 1}>First</button><button onClick={() => navigate(current - step)} disabled={current === 1}>Previous</button><span>{current}{mode === "spread" && page < total ? `–${page + 1}` : ""} / {total}</span><button onClick={() => navigate(current + step)} disabled={current + step > total}>Next</button><button onClick={() => navigate(total)} disabled={current === total}>Last</button></div>;
  return <main className="reader"><Link to="/comics">← All comics</Link><h1>{comic.title}</h1><div className="reader-modes" aria-label="Reading view">{[["page", "Single page"], ["spread", "Two pages"], ["scroll", "Scroll"]].map(([value, label]) => <button key={value} aria-pressed={mode === value} onClick={() => navigate(current, value)}>{label}</button>)}</div>{controls}<form className="reader-jump" onSubmit={(event) => { event.preventDefault(); navigate(Number.parseInt(jump, 10) || 1); }}><label htmlFor="page-number">Go to page</label><input id="page-number" type="number" min="1" max={total} required value={jump} onChange={(event) => setJump(event.target.value)} /><button>Go</button></form><div className={`reader-pages reader-${mode}`}>{(mode === "scroll" ? Array.from({ length: total }, (_, index) => index + 1) : mode === "spread" && page < total ? [page, page + 1] : [page]).map((number) => <div id={`comic-page-${number}`} key={number}><ComicImage src={url(number)} number={number} compact={mode === "scroll"} eager={number >= page && number <= page + 1} /></div>)}</div>{controls}<button onClick={share}>Share this page</button><p role="status">{shareStatus}</p><Discussion item={comic} /></main>;
}
